import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, Globe, MapPin, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { contactTranslations } from '../translations/contact';
import { useSeo } from '../hooks/useSeo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { language } = useLanguage();
  const t = contactTranslations[language];

  const isFr = language === 'fr';
  useSeo(
    isFr ? "Contact | Numérologie & accompagnement à Ibiza et en visio" : "Comprendre qui l'on est - Stef Ruiz",
    isFr ? "Contactez Stef Ruiz pour vous accompagner lors d'un changement de vie, retraite, séparation, mariage, burn-out ou remise en question, à Ibiza ou en visio." : "A product of emergent.sh"
  );
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [consent, setConsent] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Client-side validation: message must be at least 10 characters
    if (formData.message.trim().length < 10) {
      setErrorMessage(t.form.errorShortMessage);
      return;
    }
    if (!consent) {
      setErrorMessage(t.form.consentError);
      return;
    }

    try {
      const response = await axios.post(`${API}/contact`, { ...formData, consentGiven: true });
      console.log('Contact submitted:', response.data);
      setIsSubmitted(true);
      
      setTimeout(() => {
        setIsSubmitted(false);
        setConsent(false);
        setFormData({
          name: '',
          email: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting contact:', error);
      const detail = error?.response?.data?.detail;
      const isShort = Array.isArray(detail) && detail.some(d => d?.type === 'string_too_short');
      setErrorMessage(isShort ? t.form.errorShortMessage : t.form.errorGeneric);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section with Photo */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/stef-contact.jpg)',
            backgroundPosition: '50% 30%',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/50 via-gray-900/40 to-gray-900/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-lg sm:text-xl text-white/95 leading-relaxed font-light">
            {t.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Info and Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-light text-teal-600 mb-8">
                {t.info.title}
              </h2>
              
              <div className="space-y-6">
                <Card className="border-none shadow-lg bg-gradient-to-br from-teal-50 to-white">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <Mail size={24} className="text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{t.info.email}</h3>
                        <a
                          href="mailto:stefruiz04@gmail.com"
                          className="text-teal-600 hover:text-teal-700 transition-colors"
                        >
                          stefruiz04@gmail.com
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg bg-gradient-to-br from-pink-50 to-white">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                        <Phone size={24} className="text-pink-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{t.info.phone}</h3>
                        <a
                          href="tel:+34608414296"
                          className="text-pink-600 hover:text-pink-700 transition-colors"
                        >
                          +34 608 414 296
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg bg-gradient-to-br from-amber-50 to-white">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Globe size={24} className="text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{t.info.website}</h3>
                        <p className="text-amber-600">www.comprendre-qui-on-est.com</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg bg-gradient-to-br from-teal-50 to-white">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <MapPin size={24} className="text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{t.info.consultations}</h3>
                        <p className="text-gray-700">{t.info.consultationsText}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="border-none shadow-2xl">
                <CardContent className="p-8 md:p-10">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500 to-pink-500 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 size={40} className="text-white" />
                      </div>
                      <h2 className="text-3xl font-light text-teal-600 mb-4">{t.form.success}</h2>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {t.form.successMessage}
                      </p>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-3xl font-light text-teal-600 mb-2">
                        {t.form.title}
                      </h2>
                      <p className="text-gray-600 mb-8">
                        {t.form.subtitle}
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">{t.form.name}</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">{t.form.email}</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">{t.form.message}</Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            required
                            rows={5}
                            placeholder={t.form.messagePlaceholder}
                            className="border-gray-300 focus:border-teal-500 focus:ring-teal-500 resize-none"
                          />
                          <p className="text-xs text-gray-500" data-testid="contact-message-hint">{t.form.messageHint}</p>
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="contact-consent-checkbox"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 flex-shrink-0"
                          />
                          <span className="text-xs text-gray-600 leading-relaxed">
                            {t.form.consentBefore}
                            <Link to="/confidentialite" target="_blank" className="text-teal-600 underline hover:text-teal-700">
                              {t.form.consentLink}
                            </Link>
                            {t.form.consentAfter}
                          </span>
                        </label>

                        {errorMessage && (
                          <div
                            className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
                            data-testid="contact-error-message"
                          >
                            {errorMessage}
                          </div>
                        )}

                        <Button
                          type="submit"
                          size="lg"
                          data-testid="contact-submit-button"
                          className="w-full bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 text-white text-lg py-6 h-auto"
                        >
                          {t.form.button}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-pink-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-center text-teal-600 mb-12">
            {t.faq.title}
          </h2>
          
          <div className="space-y-6">
            <Card className="border-none shadow-lg bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-teal-600 mb-2">
                  {t.faq.q1.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {t.faq.q1.answer}
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-teal-600 mb-2">
                  {t.faq.q2.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {t.faq.q2.answer}
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-teal-600 mb-2">
                  {t.faq.q3.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {t.faq.q3.answer}
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-teal-600 mb-2">
                  {t.faq.q4.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {t.faq.q4.answer}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-8">
            {t.cta.title}
          </h2>
          <Link to="/booking">
            <Button size="lg" className="bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 text-white text-lg px-12 py-8 h-auto shadow-xl transition-all duration-300 hover:scale-105">
              {t.cta.button}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
